import { defineNuxtPlugin } from '#app';
import { useUserStore } from '~/store/user/userStore';
import { useDialogStore } from '~/store/dialog/dialogStore';
import { watch } from '#imports';
import { type ExportedGlobalComposer } from 'vue-i18n';
import { resolveSdkDomain, resolveSdkUrl } from '~~/package/utils/SdkUrlUtil';
import { waitForSdk } from '~~/package/utils/SdkLoaderUtil';
import { isStorybookEnvironment } from '~~/package/utils/StorybookUtil';
import {
    createSdkReconciler,
    isAnyPopupOpen,
    resolveSdkLanguage,
    resolveSdkRole,
} from '~~/package/utils/SdkObserverUtil';

// 도메인별 S3 prefix(예: /localhost/, /eu.codepresso.io/)에서 SDK를 로드한다 (설정 데이터 경로와 동일 규칙).
const ONBOARDING_SDK_URL = resolveSdkUrl('onboarding-sdk.umd.cjs');

interface NuxtI18nCustom {
    locale: string | { value: string };
    setLocale: (locale: string) => void;
    getLocaleCookie: () => string;
}

interface NuxtAppCustom {
    $i18n: ExportedGlobalComposer & NuxtI18nCustom;
}

interface ObserveHandle {
    readonly activeTour: { start: () => void; destroy: () => void; setLocale?: (locale: string) => void } | null;
    setLocale: (locale: string) => void;
    destroy: () => void;
}

interface OnboardingSdkExports {
    OnboardingTour: (new (options: { config: unknown }) => { start: () => void }) & {
        fromDomain: (
            options?: { autoStart?: boolean; onComplete?: () => void; locale?: string; role?: string },
            domain?: string
        ) => Promise<{ start: () => void; destroy: () => void; setLocale?: (locale: string) => void } | null>;
        observe: (
            options?: { autoStart?: boolean; onComplete?: () => void; locale?: string; role?: string },
            domain?: string
        ) => Promise<ObserveHandle>;
    };
    fetchConfigFromDomain: (domain?: string) => Promise<unknown>;
    getConfigUrlForDomain: (domain?: string) => string;
    injectStyles?: () => void;
    removeStyles?: () => void;
    // index.json 자동 매칭(observe)을 타지 않고 특정 투어를 이름으로 불러오는 경로.
    // OpenCode 실습 온보딩이 이 표면을 쓴다 (useOpencodeOnboarding).
    fetchConfigByUrl?: (domain: string | undefined, jsonUrl: string) => Promise<{ version?: string; steps?: unknown[] }>;
    fetchI18n?: (domain?: string, i18nUrl?: string) => Promise<unknown>;
    fetchGlobalStyle?: (domain?: string, styleUrl?: string) => Promise<string | null>;
    // 카드 테마 CSS 주입. fromDomain()/observe() 는 내부에서 부르지만 생성자 직접 사용 시엔
    // 아무도 불러주지 않으므로 호출부가 직접 넣어야 한다.
    injectGlobalStyles?: (css: string) => void;
    isCompleted?: (jsonUrl: string, version: string | undefined) => boolean;
    markCompleted?: (jsonUrl: string, version: string | undefined) => void;
}

export default defineNuxtPlugin(async (nuxtApp) => {
    if (isStorybookEnvironment()) return;

    const userStore = useUserStore();
    const dialogStore = useDialogStore();
    const $i18n = (nuxtApp as unknown as NuxtAppCustom).$i18n;

    let sdk: OnboardingSdkExports;
    try {
        sdk = (await waitForSdk<typeof window.OnboardingSdk>(
            'OnboardingSdk',
            ONBOARDING_SDK_URL
        )) as OnboardingSdkExports;
    } catch (e) {
        console.warn('[OB] onboarding SDK load failed, skipping onboarding plugin:', e);
        return;
    }

    const {
        OnboardingTour,
        fetchConfigFromDomain,
        getConfigUrlForDomain,
        injectStyles,
        removeStyles,
        fetchConfigByUrl,
        fetchI18n,
        fetchGlobalStyle,
        injectGlobalStyles,
        isCompleted,
        markCompleted,
    } = sdk;

    if (typeof injectStyles === 'function') {
        injectStyles();
    }

    const getLanguage = () => resolveSdkLanguage($i18n.locale);

    // 사용자 데이터가 로드되기 전에는 role을 undefined로 둔다.
    // SDK 필터(`!e.role || e.role === role`)에 의해 role 필드가 있는 entry
    // (예: dashboard-admin/candidate)는 자동으로 skip되고, role 무관 투어만 매칭된다.
    // userV2가 로드되면 watcher가 발화하여 올바른 role로 재 observe().
    const getRole = (): string | undefined =>
        resolveSdkRole({
            uuid: userStore.userV2.uuid,
            isAdmin: userStore.isAdminType || userStore.isSuperAdminType,
        });

    // 팝업(다이얼로그)이 하나라도 열려 있으면 true.
    // 녹화 불가 등 GenericAlert는 dialogStore.show, 멀티 다이얼로그는 multiOption[]에 쌓인다.
    const isPopupOpen = () =>
        isAnyPopupOpen({ show: dialogStore.show, multiOption: dialogStore.multiOption });

    // SDK 스크립트 URL 과 동일한 canonical 도메인. observe 에 명시하지 않으면 SDK 가 config 를
    // raw window.location.hostname 으로 로드해 www.codepresso.io / 127.0.0.1 에서 prefix 불일치로 404.
    const sdkDomain = resolveSdkDomain();

    // 현재 role/locale/팝업 상태에 맞춰 observer를 재구성한다.
    // 팝업이 하나라도 열려 있는 동안에는 투어를 숨긴 채(handle=null) 둔다.
    // SDK가 setRole을 지원하지 않으므로 destroy 후 observe()를 재호출하는 패턴(seq 가드는 createSdkReconciler 내부).
    const reconciler = createSdkReconciler<ObserveHandle>({
        observe: () =>
            OnboardingTour.observe(
                {
                    autoStart: true,
                    locale: getLanguage(),
                    role: getRole(),
                },
                sdkDomain
            ),
        isPopupOpen,
        onError: (e) => console.warn('[OB] observe() 실패:', e),
    });
    const reconcile = () => reconciler.reconcile();

    // $i18n.locale 변경 감지 (사용자가 직접 언어 전환 시)
    watch(
        () => {
            const loc = $i18n.locale;
            return (typeof loc === 'string' ? loc : (loc as any)?.value ?? '') || 'en';
        },
        (newLocale) => {
            reconciler.setLocale(newLocale.toLowerCase());
        }
    );

    // userStore.userV2.locale 변경 감지 (회원 데이터 로드 후 locale 확정 시)
    watch(
        () => userStore.userV2.locale,
        () => {
            reconciler.setLocale(getLanguage());
        }
    );

    // role 변경 감지 + 최초 1회 init.
    watch(() => getRole(), () => reconcile(), { immediate: true });

    // 팝업 노출 상태 감지: 팝업이 열리면 투어를 숨기고(destroy), 모두 닫히면 다시 observe.
    watch(() => isPopupOpen(), () => reconcile());

    return {
        provide: {
            onboardingSdk: {
                OnboardingTour,
                fetchConfigFromDomain,
                getConfigUrlForDomain,
                injectStyles,
                removeStyles,
                fetchConfigByUrl,
                fetchI18n,
                fetchGlobalStyle,
                injectGlobalStyles,
                isCompleted,
                markCompleted,
            },
        },
    };
});
